import { memo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import type { FieldErrors } from 'react-hook-form';
import type { Provider } from '../types';
import { CLAIM_TYPE_PROVIDER_CONFIG, getProviderMode } from '../constants/providerConfig';
import type { ASAClassificationOption } from '../constants/asa';
import { getClaimTypeIcon } from './claimTypeIcons';
import ClaimTypeProviderPanel, { type ClaimTypeAssignment, type ClaimProviderSuggestion } from './ClaimTypeProviderPanel';
import CodingSection from './CodingSection';
import SubblockHeading from './SubblockHeading';
import type { FormFieldSetting } from '../constants/encounterFieldsConfig';

export interface ClaimBillTypeCardProps {
  /** Bill-type code (OFFICE_VISIT, SURGICAL, ANESTHESIA, FACILITY, ...). */
  claimType: string;
  /** Remove this bill-type section (toggles the type off). */
  onRemove: () => void;

  assignment: ClaimTypeAssignment | undefined;
  onAssignmentChange: (next: ClaimTypeAssignment | null) => void;
  providers: Provider[];
  providersLoading: boolean;
  providersError: string | null;
  providerValidationError?: string | null;
  /** Hides the Providers subblock when the practice makes providers not-visible.
   *  Defaults to true so edit-claim (fixed requiredness) is unaffected. */
  showProviders?: boolean;
  suggestions?: ClaimProviderSuggestion[];
  onLocate?: (documentId: string, page: number, bbox: NonNullable<ClaimProviderSuggestion['source']>['bbox'], label: string) => void;

  control: unknown;
  /** Form path prefix for this claim's coding object, e.g. `claimTypes.0.coding`. */
  fieldPrefix: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  codingErrors?: FieldErrors<any>;
  asaOptions: readonly ASAClassificationOption[];
  /** Encounter Date of Service (ISO) — for the ASA cross-day computation. */
  dateOfService: string;
  /** Feature 043 — per-field visible/required config for this claim type's coding fields. */
  fields?: Partial<Record<string, FormFieldSetting>>;
}

/**
 * A single selected claim type rendered as a full-width stacked "bill type" card
 * (submit-claim / edit-claim, prototype "Approach B"): an icon + label + Remove header,
 * then a Providers subblock (if applicable) and a Coding subblock (ICD-10 + CPT, plus ASA
 * for Anesthesia). FACILITY has no providers (per CLAIM_TYPE_PROVIDER_CONFIG.mode: 'none')
 * but does have coding fields like other billable types. Sections are hidden when the
 * practice makes them not-visible (feature 043).
 */
function ClaimBillTypeCard({
  claimType,
  onRemove,
  assignment,
  onAssignmentChange,
  providers,
  providersLoading,
  providersError,
  providerValidationError,
  showProviders = true,
  suggestions,
  onLocate,
  control,
  fieldPrefix,
  codingErrors,
  asaOptions,
  dateOfService,
  fields,
}: ClaimBillTypeCardProps) {
  const label = CLAIM_TYPE_PROVIDER_CONFIG[claimType]?.label ?? claimType;
  const showAsa = claimType === 'ANESTHESIA';
  const hasProviders = getProviderMode(claimType) !== 'none';

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.paper' }}>
      {/* Header — icon + label + Remove */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2.5,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 1,
            bgcolor: '#f5f5f5',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {getClaimTypeIcon(claimType)}
        </Box>
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 500, color: 'text.primary' }} noWrap>
          {label}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<CloseIcon sx={{ fontSize: 17 }} />}
          onClick={onRemove}
          sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary', borderColor: 'divider' }}
        >
          Remove
        </Button>
      </Box>

      {/* Body */}
      <Box sx={{ p: 3 }}>
        {/* Providers subblock — hidden when the practice makes providers not-visible (feature 043)
            or when the claim type has no providers (e.g. FACILITY with mode: 'none'). */}
        {showProviders && hasProviders && (
          <>
            <SubblockHeading icon={<GroupsOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />} title="Providers" />
            <ClaimTypeProviderPanel
              claimType={claimType}
              assignment={assignment}
              providers={providers}
              onAssignmentChange={onAssignmentChange}
              loading={providersLoading}
              error={providersError}
              validationError={providerValidationError}
              suggestions={suggestions}
              onLocate={onLocate}
            />
          </>
        )}

        {/* Coding subblock — self-hides when every field is not-visible-and-empty (feature
            043). The top divider (owned by CodingSection) only applies when the Providers
            subblock precedes it. */}
        <CodingSection
          control={control}
          fieldPrefix={fieldPrefix}
          codingErrors={codingErrors}
          showAsa={showAsa}
          asaOptions={asaOptions}
          dateOfService={dateOfService}
          fields={fields}
          showDividerAbove={showProviders && hasProviders}
        />
      </Box>
    </Box>
  );
}

export default memo(ClaimBillTypeCard);
